import { useMemo, useState } from 'react';

import { RecommenderExtendedProfile } from '@youscience/commonapp-service-common';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore from '@store/RecommenderProfileStore';
import useNotificationStore from '@store/NotificationStore';

import {
  updateClassRankState,
  updateGPARankState,
  updateSchoolDetailsCounselorState,
  updateSchoolProfileState,
  updateTranscriptsState,
} from './saveUtils';

import { SchoolProfileSection, SchoolProfileState, allSections, schoolProfileSections } from './types';
import { UPDATE_ERROR_NOTIFICATION } from '@pages/StudentDetailsPage/components/common/formUtils';

interface SectionState {
  formState: SchoolProfileState;
  expandedSection: SchoolProfileSection | null;
  savedSections: Record<SchoolProfileSection, boolean>;
}
export const useSaveProfile = () => {
  const { recommenderProfile, saveRecommenderProfile } = useRecommenderStore((store) => store);

  const {
    formState: {
      personalDetails: personalDetailsFormState,
      schoolProfile: schoolProfileFormState,
      schoolDetailsCounselor: schoolDetailsCounselorFormState,
      classRank: classRankFormState,
      gpa: gpaFormState,
      transcripts: transcriptsFormState,
    },
  } = useRecommenderProfileStore((store) => store);

  const schoolProfileState: SchoolProfileState = {
    [SchoolProfileSection.SchoolDetailsCounselor]: null,
    [SchoolProfileSection.SchoolProfile]: null,
    [SchoolProfileSection.ClassRank]: null,
    [SchoolProfileSection.GPA]: null,
    [SchoolProfileSection.Transcripts]: null,
  };

  const initialSavedSections = Object.values(allSections).reduce(
    (acc, section) => {
      acc[section as SchoolProfileSection] = false;
      return acc;
    },
    {} as Record<SchoolProfileSection, boolean>,
  );

  const [sectionState, setSectionState] = useState<SectionState>({
    formState: schoolProfileState,
    expandedSection: null,
    savedSections: initialSavedSections,
  });

  const allFormsAreValid = useMemo(() => {
    return (
      personalDetailsFormState.isFormValid &&
      schoolProfileFormState.isFormValid &&
      schoolDetailsCounselorFormState.isFormValid &&
      classRankFormState.isFormValid &&
      gpaFormState.isFormValid &&
      transcriptsFormState.isFormValid
    );
  }, [
    personalDetailsFormState,
    schoolProfileFormState,
    schoolDetailsCounselorFormState,
    classRankFormState,
    gpaFormState,
    transcriptsFormState,
  ]);

  const hasChanges = useMemo(() => {
    return (
      personalDetailsFormState.hasChanged ||
      schoolProfileFormState.hasChanged ||
      schoolDetailsCounselorFormState.hasChanged ||
      classRankFormState.hasChanged ||
      gpaFormState.hasChanged ||
      transcriptsFormState.hasChanged
    );
  }, [
    personalDetailsFormState,
    schoolProfileFormState,
    schoolDetailsCounselorFormState,
    classRankFormState,
    gpaFormState,
    transcriptsFormState,
  ]);

  const updateCounselorState = async (recommenderProfile: RecommenderExtendedProfile) => {
    const schoolProfileSectionsToSave = [];
    let schoolProfileData = { ...recommenderProfile.schoolProfile };

    const updateSection = (options: Record<string, unknown>) => {
      return { ...schoolProfileData, ...options };
    };

    if (schoolDetailsCounselorFormState.hasChanged && schoolDetailsCounselorFormState.isFormValid) {
      schoolProfileSectionsToSave.push(SchoolProfileSection.SchoolDetailsCounselor);
      schoolProfileData = updateSection(updateSchoolDetailsCounselorState(schoolDetailsCounselorFormState));
    }

    if (schoolProfileFormState.hasChanged && schoolProfileFormState.isFormValid) {
      schoolProfileSectionsToSave.push(SchoolProfileSection.SchoolProfile);
      schoolProfileData = updateSection(updateSchoolProfileState(recommenderProfile, schoolProfileFormState));
    }

    if (classRankFormState.hasChanged && classRankFormState.isFormValid) {
      schoolProfileSectionsToSave.push(SchoolProfileSection.ClassRank);
      schoolProfileData = updateSection(updateClassRankState(classRankFormState));
    }

    if (gpaFormState.hasChanged && gpaFormState.isFormValid) {
      schoolProfileSectionsToSave.push(SchoolProfileSection.GPA);
      schoolProfileData = updateSection(updateGPARankState(gpaFormState));
    }

    if (transcriptsFormState.hasChanged && transcriptsFormState.isFormValid) {
      schoolProfileSectionsToSave.push(SchoolProfileSection.Transcripts);
      schoolProfileData = updateSection(updateTranscriptsState(transcriptsFormState));
    }

    return {
      schoolProfileSectionsToSave,
      updates: schoolProfileData,
    };
  };

  const saveProfile = async (isUpdateForAllStudents?: boolean) => {
    let schoolProfileData = {};
    const teacherData = {};
    const sectionsToSave: SchoolProfileSection[] = [];

    const { schoolProfileSectionsToSave, updates: schoolProfileUpdates } =
      await updateCounselorState(recommenderProfile);

    sectionsToSave.push(...schoolProfileSectionsToSave);
    schoolProfileData = schoolProfileUpdates;

    if (sectionsToSave.length > 0) {
      let data = {};

      if (Object.keys(schoolProfileData).length > 0) {
        data = { ...data, schoolProfile: schoolProfileData };
      }

      if (Object.keys(teacherData).length > 0) {
        data = { ...data, teacherProfile: teacherData };
      }

      if (Object.keys(data).length === 0) {
        return;
      }

      const preparedUpdateForms: Record<string, boolean> = {
        [SchoolProfileSection.ClassRank]: false,
        [SchoolProfileSection.GPA]: false,
        [SchoolProfileSection.Transcripts]: false,
      };

      sectionsToSave.forEach((section) => {
        preparedUpdateForms[section] = true;
      });

      const updateForms = isUpdateForAllStudents ? preparedUpdateForms : {};

      try {
        await saveRecommenderProfile(data, 'School Profile', updateForms);
      } catch (error) {
        useNotificationStore.getState().notify(UPDATE_ERROR_NOTIFICATION);
      }

      sectionsToSave.forEach((section) => {
        const state = sectionState.formState[section];
        if (state) {
          state.hasChanged = false;
          state.isFormValid = true;
        }

        setSectionState((prevState) => ({
          ...prevState,
          savedSections: {
            ...prevState.savedSections,
            [section]: true,
          },
        }));
      });

      useNotificationStore.getState().notify({
        message: isUpdateForAllStudents
          ? 'Updates to School profile have been applied to student reports.'
          : 'School Profile updates have been saved.',
        severity: 'success',
      });
    }
  };

  const saveAndContinue = async (currentSection: SchoolProfileSection) => {
    await saveProfile();

    const sections = [...schoolProfileSections];

    const currentSectionIndex = sections.indexOf(currentSection);

    let expandedSection: SectionState['expandedSection'] = null;
    if (currentSectionIndex < sections.length - 1) {
      expandedSection = sections[currentSectionIndex + 1];
    }

    setSectionState((prevState) => ({
      ...prevState,
      expandedSection,
    }));
  };

  const toggleExpandedSelection = (section: SchoolProfileSection) => {
    let expandedSection: SectionState['expandedSection'] = null;

    if (sectionState.expandedSection !== section) {
      expandedSection = section;
    } else {
      expandedSection = null;
    }

    setSectionState((prevState) => ({
      ...prevState,
      expandedSection,
    }));
  };

  return {
    sectionState,
    saveProfile,
    saveAndContinue,
    toggleExpandedSelection,
    allFormsAreValid,
    hasChanges,
  };
};
